import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import {
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
} from '../plan/floor-plan-element';

/**
 * Paint order for non-table draw items, aligned with v3 `ui-view-tables.ts`
 * (`GENERIC` … `BAR`, bottom → top).
 */
export const FLOOR_PLAN_DRAW_ITEM_TYPE_ORDER = [
  'GENERIC',
  'DOOR',
  'WINDOW',
  'STAIRS',
  'SOFA',
  'FOOTBALL',
  'BILLIARD',
  'BAR',
] as const;

export type FloorPlanDrawItemTypeKey = (typeof FLOOR_PLAN_DRAW_ITEM_TYPE_ORDER)[number];

function drawItemTypeKeyForElement(el: UppFloorPlanElement): FloorPlanDrawItemTypeKey {
  if (el instanceof UppFloorPlanBar) {
    return 'BAR';
  }
  if (el instanceof UppFloorPlanArchitecture) {
    switch (el.architectureClass) {
      case 'door':
        return 'DOOR';
      case 'window':
        return 'WINDOW';
      case 'stairs':
        return 'STAIRS';
      default:
        return 'GENERIC';
    }
  }
  if (el instanceof UppFloorPlanFurniture) {
    switch (el.furnitureClass) {
      case 'sofa':
        return 'SOFA';
      case 'football':
        return 'FOOTBALL';
      case 'billiard':
        return 'BILLIARD';
      default:
        return 'GENERIC';
    }
  }
  return 'GENERIC';
}

/**
 * Legacy type-based layer index for **draw pieces** (not tables or vertices).
 * Used only as a **tie-breaker** when {@link UppFloorPlanElement.zIndex} values are equal.
 */
export function floorPlanDrawItemZIndex(el: UppFloorPlanElement): number {
  const key = drawItemTypeKeyForElement(el);
  const i = FLOOR_PLAN_DRAW_ITEM_TYPE_ORDER.indexOf(key);
  return i >= 0 ? i : 0;
}

/** Tie-breaker for draw pieces when `element.zIndex` ties (v3 type order). */
export function compareFloorPlanDrawItemsPaintOrder(a: UppFloorPlanElement, b: UppFloorPlanElement): number {
  return floorPlanDrawItemZIndex(a) - floorPlanDrawItemZIndex(b);
}
